import { BookingRecurrence } from './translation_namespace/availabilities_namespace';
import { RecurrenceRuleLabelTranslation } from './translation_namespace/recurrence_rule_namespace';

/**
 * Sous-ensemble RRULE (RFC 5545) whitelisté pour la récurrence des rendez-vous.
 *
 * Contrat de stockage (appointment.recurrence_rrule) :
 * - Forme canonique : ordre fixe `FREQ;INTERVAL;BYDAY;BYMONTHDAY;BYSETPOS;UNTIL;COUNT`,
 *   `INTERVAL=1` omis, `BYDAY` trié lundi-d'abord, JAMAIS de DTSTART (l'ancre = start_pro).
 * - `UNTIL` canonique = `YYYYMMDD` (date seule), INCLUSIF, interprété en fin de journée
 *   dans l'heure murale du professionnel au moment de l'expansion.
 * - Mensuel ancré un 29/30/31 : clamp au dernier jour du mois via la forme standard
 *   `BYMONTHDAY=28,...,D;BYSETPOS=-1` (un `BYMONTHDAY` simple >= 29 est rejeté pour
 *   ne jamais introduire la sémantique RFC « skip » en base).
 * - `FREQ=YEARLY` : ancre-implicite (mois/jour de start_pro) ; un 29 février ne se
 *   produit que les années bissextiles.
 *
 * Convention jours de semaine : Date.getDay() — 0=dimanche … 6=samedi (cohérente avec
 * availability_slot.day). Les conversions vers les codes RFC (MO..SU) restent internes.
 */

export enum RecurrenceFreq {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum RecurrenceEndMode {
  NEVER = 'NEVER',
  UNTIL_DATE = 'UNTIL_DATE',
  AFTER_COUNT = 'AFTER_COUNT',
}

export enum RecurrenceMonthlyMode {
  BY_MONTHDAY = 'BY_MONTHDAY',
  BY_ORDINAL_WEEKDAY = 'BY_ORDINAL_WEEKDAY',
  /**
   * « Nème semaine du mois », tous les jours de semaine au même ordinal — forme RFC
   * `BYDAY=NMO,NTU,NWE,NTH,NFR,NSA,NSU`. La 1ère occurrence de chaque jour tombe toujours
   * dans les 7 premiers jours du mois → `weekOfMonth=1` ≡ jours 1-7, `-1` ≡ les 7 derniers.
   * Aucun jour n'est privilégié : pur bucket semaine-du-mois (usage : activation des dispos,
   * où les JOURS appartiennent aux plages horaires, pas à la règle).
   */
  BY_WEEK_OF_MONTH = 'BY_WEEK_OF_MONTH',
}

export type RecurrenceOrdinal = 1 | 2 | 3 | 4 | -1;

export interface RecurrenceRuleObject {
  freq: RecurrenceFreq;
  /** 1..99 — 1 est omis à la sérialisation. */
  interval: number;
  /** WEEKLY uniquement — convention Date.getDay(), valeurs uniques. Absent = jour de l'ancre. */
  byWeekdays?: number[];
  /** MONTHLY uniquement — absent = jour du mois de l'ancre (ancre-implicite). */
  monthlyMode?: RecurrenceMonthlyMode;
  /** MONTHLY BY_MONTHDAY — 1..31 ; >= 29 sérialisé via le trick clamp `28..D;BYSETPOS=-1`. */
  byMonthDay?: number;
  /** MONTHLY BY_ORDINAL_WEEKDAY — ex. { ordinal: 2, weekday: 4 } = « le 2e jeudi ». */
  byOrdinal?: { ordinal: RecurrenceOrdinal; weekday: number };
  /** MONTHLY BY_WEEK_OF_MONTH — 1..4 ou -1 (« dernière »). Ex. 1 = la 1ère semaine du mois. */
  weekOfMonth?: RecurrenceOrdinal;
  endMode: RecurrenceEndMode;
  /** `YYYYMMDD` si endMode=UNTIL_DATE — inclusif, heure murale du pro. */
  until?: string;
  /** 1..366 si endMode=AFTER_COUNT — COUNT=1 est réservé aux splits système (le picker n'envoie jamais 1). */
  count?: number;
}

export interface RecurrenceAnchor {
  /** Date.getDay() : 0=dimanche … 6=samedi. */
  weekday: number;
  /** 1..31. */
  monthday: number;
  /** true si l'ancre est la dernière occurrence de son jour de semaine dans son mois. */
  isLastWeekdayOfMonth: boolean;
}

export class InvalidRecurrenceRuleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidRecurrenceRuleError';
  }
}

export const RECURRENCE_INTERVAL_MIN = 1;
export const RECURRENCE_INTERVAL_MAX = 99;
export const RECURRENCE_COUNT_MIN = 1;
export const RECURRENCE_COUNT_MAX = 366;
export const RECURRENCE_RRULE_MAX_LENGTH = 255;

/** Index = Date.getDay(). */
export const RRULE_WEEKDAY_CODES: readonly string[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const WHITELISTED_PARTS: readonly string[] = ['FREQ', 'INTERVAL', 'BYDAY', 'BYMONTHDAY', 'BYSETPOS', 'UNTIL', 'COUNT'];

/** Tri canonique lundi-d'abord (MO,TU,WE,TH,FR,SA,SU). */
const mondayFirstRank = (weekday: number): number => (weekday + 6) % 7;

const weekdayFromCode = (code: string): number => RRULE_WEEKDAY_CODES.indexOf(code);

const isValidWeekday = (weekday: number): boolean => Number.isInteger(weekday) && weekday >= 0 && weekday <= 6;

// Déclaration de fonction (pas une const fléchée) : nécessaire pour que TypeScript
// reconnaisse l'appel comme terminal (`never`) et fasse le narrowing après les gardes.
function throwInvalid(reason: string): never {
  throw new InvalidRecurrenceRuleError(reason);
}

const isValidCalendarDate = (year: number, month: number, day: number): boolean => {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

export function getRecurrenceAnchorFromWallDate(wallDate: Date): RecurrenceAnchor {
  const monthday = wallDate.getDate();
  const daysInMonth = new Date(wallDate.getFullYear(), wallDate.getMonth() + 1, 0).getDate();
  return {
    weekday: wallDate.getDay(),
    monthday,
    isLastWeekdayOfMonth: monthday + 7 > daysInMonth,
  };
}

/** Ordinal effectif d'une ancre dans son mois (« 2e jeudi », « dernier jeudi »). */
export function ordinalFromRecurrenceAnchor(anchor: RecurrenceAnchor): RecurrenceOrdinal {
  if (anchor.isLastWeekdayOfMonth) return -1;
  const ordinal = Math.ceil(anchor.monthday / 7);
  return (ordinal >= 5 ? -1 : ordinal) as RecurrenceOrdinal;
}

function validateRuleObject(rule: RecurrenceRuleObject): void {
  if (!Object.values(RecurrenceFreq).includes(rule.freq)) throwInvalid(`Unknown FREQ '${rule.freq}'`);
  if (
    !Number.isInteger(rule.interval) ||
    rule.interval < RECURRENCE_INTERVAL_MIN ||
    rule.interval > RECURRENCE_INTERVAL_MAX
  ) {
    throwInvalid(`INTERVAL must be ${RECURRENCE_INTERVAL_MIN}..${RECURRENCE_INTERVAL_MAX}`);
  }

  if (rule.byWeekdays !== undefined) {
    if (rule.freq !== RecurrenceFreq.WEEKLY) throwInvalid('byWeekdays is only allowed with FREQ=WEEKLY');
    if (rule.byWeekdays.length === 0) throwInvalid('byWeekdays must not be empty');
    if (rule.byWeekdays.some((weekday): boolean => !isValidWeekday(weekday))) throwInvalid('Invalid weekday value');
    if (new Set(rule.byWeekdays).size !== rule.byWeekdays.length) throwInvalid('Duplicate weekday value');
  }

  const hasMonthDay = rule.byMonthDay !== undefined;
  const hasOrdinal = rule.byOrdinal !== undefined;
  const hasWeekOfMonth = rule.weekOfMonth !== undefined;
  if (
    (hasMonthDay || hasOrdinal || hasWeekOfMonth || rule.monthlyMode !== undefined) &&
    rule.freq !== RecurrenceFreq.MONTHLY
  ) {
    throwInvalid('Monthly parts are only allowed with FREQ=MONTHLY');
  }
  if ([hasMonthDay, hasOrdinal, hasWeekOfMonth].filter(Boolean).length > 1) {
    throwInvalid('byMonthDay, byOrdinal and weekOfMonth are mutually exclusive');
  }
  if (rule.monthlyMode === RecurrenceMonthlyMode.BY_MONTHDAY && !hasMonthDay) throwInvalid('byMonthDay is required');
  if (rule.monthlyMode === RecurrenceMonthlyMode.BY_ORDINAL_WEEKDAY && !hasOrdinal)
    throwInvalid('byOrdinal is required');
  if (rule.monthlyMode === RecurrenceMonthlyMode.BY_WEEK_OF_MONTH && !hasWeekOfMonth)
    throwInvalid('weekOfMonth is required');
  if (
    rule.byMonthDay !== undefined &&
    (!Number.isInteger(rule.byMonthDay) || rule.byMonthDay < 1 || rule.byMonthDay > 31)
  ) {
    throwInvalid('byMonthDay must be 1..31');
  }
  if (rule.byOrdinal) {
    const { ordinal, weekday } = rule.byOrdinal;
    if (![1, 2, 3, 4, -1].includes(ordinal)) throwInvalid('Ordinal must be 1..4 or -1');
    if (!isValidWeekday(weekday)) throwInvalid('Invalid ordinal weekday');
  }
  if (hasWeekOfMonth && ![1, 2, 3, 4, -1].includes(rule.weekOfMonth as number)) {
    throwInvalid('weekOfMonth must be 1..4 or -1');
  }

  if (rule.endMode === RecurrenceEndMode.UNTIL_DATE) {
    if (rule.count !== undefined) throwInvalid('UNTIL and COUNT are mutually exclusive');
    if (!rule.until || !/^\d{8}$/.test(rule.until)) throwInvalid('until must be YYYYMMDD');
    const year = Number(rule.until.slice(0, 4));
    const month = Number(rule.until.slice(4, 6));
    const day = Number(rule.until.slice(6, 8));
    if (!isValidCalendarDate(year, month, day)) throwInvalid(`Invalid UNTIL date '${rule.until}'`);
  } else if (rule.endMode === RecurrenceEndMode.AFTER_COUNT) {
    if (rule.until !== undefined) throwInvalid('UNTIL and COUNT are mutually exclusive');
    if (
      rule.count === undefined ||
      !Number.isInteger(rule.count) ||
      rule.count < RECURRENCE_COUNT_MIN ||
      rule.count > RECURRENCE_COUNT_MAX
    ) {
      throwInvalid(`COUNT must be ${RECURRENCE_COUNT_MIN}..${RECURRENCE_COUNT_MAX}`);
    }
  } else if (rule.endMode === RecurrenceEndMode.NEVER) {
    if (rule.until !== undefined || rule.count !== undefined) throwInvalid('NEVER end mode forbids until/count');
  } else {
    throwInvalid(`Unknown end mode '${rule.endMode}'`);
  }
}

/**
 * Parse un string RRULE vers l'objet structuré. Whitelist stricte : toute partie,
 * valeur ou combinaison hors contrat lève `InvalidRecurrenceRuleError`.
 * Tolérant sur la casse, les espaces et l'ordre des parties (normalisés au parse).
 */
export function parseRRuleString(rrule: string): RecurrenceRuleObject {
  const trimmed = (rrule ?? '').trim();
  if (!trimmed) throwInvalid('Empty rule');
  if (trimmed.length > RECURRENCE_RRULE_MAX_LENGTH) throwInvalid('Rule is too long');

  const parts = new Map<string, string>();
  for (const rawPart of trimmed.split(';')) {
    const part = rawPart.trim();
    if (!part) throwInvalid('Empty rule part');
    const separatorIndex = part.indexOf('=');
    if (separatorIndex <= 0) throwInvalid(`Malformed rule part '${part}'`);
    const key = part.slice(0, separatorIndex).trim().toUpperCase();
    const value = part
      .slice(separatorIndex + 1)
      .trim()
      .toUpperCase();
    if (!WHITELISTED_PARTS.includes(key)) throwInvalid(`Unsupported rule part '${key}'`);
    if (parts.has(key)) throwInvalid(`Duplicate rule part '${key}'`);
    if (!value) throwInvalid(`Empty value for '${key}'`);
    parts.set(key, value);
  }

  const freqValue = parts.get('FREQ');
  if (!freqValue || !Object.values(RecurrenceFreq).includes(freqValue as RecurrenceFreq)) {
    throwInvalid(`Missing or unsupported FREQ '${freqValue ?? ''}'`);
  }
  const freq = freqValue as RecurrenceFreq;

  let interval = 1;
  const intervalValue = parts.get('INTERVAL');
  if (intervalValue !== undefined) {
    if (!/^\d+$/.test(intervalValue)) throwInvalid(`Invalid INTERVAL '${intervalValue}'`);
    interval = Number(intervalValue);
  }

  const rule: RecurrenceRuleObject = { freq, interval, endMode: RecurrenceEndMode.NEVER };

  const byDayValue = parts.get('BYDAY');
  if (byDayValue !== undefined) {
    const tokens = byDayValue.split(',').map((token): string => token.trim());
    if (freq === RecurrenceFreq.WEEKLY) {
      const weekdays = tokens.map((token): number => {
        if (!/^(SU|MO|TU|WE|TH|FR|SA)$/.test(token)) throwInvalid(`Invalid BYDAY token '${token}' for FREQ=WEEKLY`);
        return weekdayFromCode(token);
      });
      rule.byWeekdays = weekdays;
    } else if (freq === RecurrenceFreq.MONTHLY) {
      const parsedTokens = tokens.map((token): { ordinal: RecurrenceOrdinal; weekday: number } => {
        const match = /^(-1|[1-4])(SU|MO|TU|WE|TH|FR|SA)$/.exec(token);
        if (!match) throwInvalid(`Invalid ordinal BYDAY token '${token}'`);
        return { ordinal: Number(match[1]) as RecurrenceOrdinal, weekday: weekdayFromCode(match[2]) };
      });
      if (parsedTokens.length === 1) {
        rule.byOrdinal = parsedTokens[0];
        rule.monthlyMode = RecurrenceMonthlyMode.BY_ORDINAL_WEEKDAY;
      } else {
        // « Nème semaine du mois » : les 7 jours de semaine au MÊME ordinal (bucket
        // semaine, aucun jour privilégié). Toute autre combinaison multi-token est rejetée.
        const ordinals = new Set(parsedTokens.map((token): RecurrenceOrdinal => token.ordinal));
        const weekdays = new Set(parsedTokens.map((token): number => token.weekday));
        if (parsedTokens.length !== 7 || ordinals.size !== 1 || weekdays.size !== 7) {
          throwInvalid('FREQ=MONTHLY BYDAY must be one ordinal weekday or the 7 weekdays at one ordinal');
        }
        rule.weekOfMonth = parsedTokens[0].ordinal;
        rule.monthlyMode = RecurrenceMonthlyMode.BY_WEEK_OF_MONTH;
      }
    } else {
      throwInvalid(`BYDAY is not allowed with FREQ=${freq}`);
    }
  }

  const byMonthDayValue = parts.get('BYMONTHDAY');
  if (byMonthDayValue !== undefined) {
    if (freq !== RecurrenceFreq.MONTHLY) throwInvalid(`BYMONTHDAY is not allowed with FREQ=${freq}`);
    if (rule.byOrdinal || rule.weekOfMonth !== undefined) throwInvalid('BYMONTHDAY and BYDAY are mutually exclusive');
    const values = byMonthDayValue.split(',').map((token): number => {
      if (!/^\d+$/.test(token.trim())) throwInvalid(`Invalid BYMONTHDAY value '${token}'`);
      return Number(token.trim());
    });
    if (values.length === 1) {
      // Un BYMONTHDAY simple >= 29 aurait la sémantique RFC « skip » (mois plus courts
      // sans occurrence) — notre convention produit est le clamp, forme trick obligatoire.
      if (values[0] < 1 || values[0] > 28)
        throwInvalid('Single BYMONTHDAY must be 1..28 (use the clamp form for 29..31)');
      if (parts.has('BYSETPOS')) throwInvalid('BYSETPOS is not allowed with a single BYMONTHDAY');
      rule.byMonthDay = values[0];
    } else {
      // Forme clamp : liste consécutive 28..D (D ∈ 29..31) + BYSETPOS=-1 obligatoire.
      const anchorDay = values[values.length - 1];
      if (anchorDay < 29 || anchorDay > 31) throwInvalid('Clamp BYMONTHDAY list must end with 29..31');
      const expected = Array.from({ length: anchorDay - 27 }, (_, index): number => 28 + index);
      if (values.length !== expected.length || values.some((value, index): boolean => value !== expected[index])) {
        throwInvalid(`Clamp BYMONTHDAY list must be 28..${anchorDay}`);
      }
      if (parts.get('BYSETPOS') !== '-1') throwInvalid('Clamp BYMONTHDAY list requires BYSETPOS=-1');
      rule.byMonthDay = anchorDay;
    }
    rule.monthlyMode = RecurrenceMonthlyMode.BY_MONTHDAY;
  } else if (parts.has('BYSETPOS')) {
    throwInvalid('BYSETPOS is only allowed with the clamp BYMONTHDAY form');
  }

  const untilValue = parts.get('UNTIL');
  const countValue = parts.get('COUNT');
  if (untilValue !== undefined && countValue !== undefined) throwInvalid('UNTIL and COUNT are mutually exclusive');
  if (untilValue !== undefined) {
    const match = /^(\d{8})(T\d{6}Z?)?$/.exec(untilValue);
    if (!match) throwInvalid(`Invalid UNTIL '${untilValue}'`);
    rule.endMode = RecurrenceEndMode.UNTIL_DATE;
    rule.until = match[1];
  } else if (countValue !== undefined) {
    if (!/^\d+$/.test(countValue)) throwInvalid(`Invalid COUNT '${countValue}'`);
    rule.endMode = RecurrenceEndMode.AFTER_COUNT;
    rule.count = Number(countValue);
  }

  validateRuleObject(rule);
  if (rule.byWeekdays) {
    rule.byWeekdays = [...rule.byWeekdays].sort((a, b): number => mondayFirstRank(a) - mondayFirstRank(b));
  }
  return rule;
}

/** Sérialise vers la forme canonique. Valide l'objet et lève `InvalidRecurrenceRuleError` sinon. */
export function serializeRecurrenceRule(rule: RecurrenceRuleObject): string {
  validateRuleObject(rule);
  const parts: string[] = [`FREQ=${rule.freq}`];
  if (rule.interval > 1) parts.push(`INTERVAL=${rule.interval}`);

  if (rule.byWeekdays && rule.byWeekdays.length > 0) {
    const codes = [...rule.byWeekdays]
      .sort((a, b): number => mondayFirstRank(a) - mondayFirstRank(b))
      .map((weekday): string => RRULE_WEEKDAY_CODES[weekday]);
    parts.push(`BYDAY=${codes.join(',')}`);
  }
  if (rule.byOrdinal) {
    parts.push(`BYDAY=${rule.byOrdinal.ordinal}${RRULE_WEEKDAY_CODES[rule.byOrdinal.weekday]}`);
  }
  if (rule.weekOfMonth !== undefined) {
    // Les 7 jours triés lundi-d'abord, chacun préfixé du même ordinal (bucket semaine-du-mois).
    const codes = [0, 1, 2, 3, 4, 5, 6]
      .sort((a, b): number => mondayFirstRank(a) - mondayFirstRank(b))
      .map((weekday): string => `${rule.weekOfMonth}${RRULE_WEEKDAY_CODES[weekday]}`);
    parts.push(`BYDAY=${codes.join(',')}`);
  }
  if (rule.byMonthDay !== undefined) {
    if (rule.byMonthDay <= 28) {
      parts.push(`BYMONTHDAY=${rule.byMonthDay}`);
    } else {
      const days = Array.from({ length: rule.byMonthDay - 27 }, (_, index): number => 28 + index);
      parts.push(`BYMONTHDAY=${days.join(',')}`);
      parts.push('BYSETPOS=-1');
    }
  }
  if (rule.endMode === RecurrenceEndMode.UNTIL_DATE) parts.push(`UNTIL=${rule.until}`);
  if (rule.endMode === RecurrenceEndMode.AFTER_COUNT) parts.push(`COUNT=${rule.count}`);
  return parts.join(';');
}

/** Parse + re-sérialise : retourne la forme canonique ou lève `InvalidRecurrenceRuleError`. */
export function validateAndCanonicalizeRRuleString(rrule: string): string {
  return serializeRecurrenceRule(parseRRuleString(rrule));
}

/** Enum résumé (colonne SQL `recurrence`) dérivé du FREQ de la règle. */
export function bookingRecurrenceFromRRule(rrule: string): BookingRecurrence {
  const rule = parseRRuleString(rrule);
  switch (rule.freq) {
    case RecurrenceFreq.DAILY:
      return BookingRecurrence.EVERYDAY;
    case RecurrenceFreq.WEEKLY:
      return BookingRecurrence.EVERY_WEEK;
    case RecurrenceFreq.MONTHLY:
      return BookingRecurrence.EVERY_MONTH;
    case RecurrenceFreq.YEARLY:
      return BookingRecurrence.EVERY_YEAR;
  }
}

/**
 * Synthèse d'une règle depuis l'enum seul (clients legacy/enum-only, lignes
 * `recurrence_rrule NULL`). `anchorMonthDay` est requis pour EVERY_MONTH.
 */
export function rruleFromBookingRecurrence(
  recurrence: BookingRecurrence | string | null | undefined,
  anchorMonthDay?: number,
): string | null {
  switch (recurrence) {
    case BookingRecurrence.EVERYDAY:
      return 'FREQ=DAILY';
    case BookingRecurrence.EVERY_WEEK:
      return 'FREQ=WEEKLY';
    case BookingRecurrence.EVERY_MONTH: {
      if (anchorMonthDay === undefined) throwInvalid('anchorMonthDay is required to synthesize EVERY_MONTH');
      return serializeRecurrenceRule({
        freq: RecurrenceFreq.MONTHLY,
        interval: 1,
        monthlyMode: RecurrenceMonthlyMode.BY_MONTHDAY,
        byMonthDay: anchorMonthDay,
        endMode: RecurrenceEndMode.NEVER,
      });
    }
    case BookingRecurrence.EVERY_YEAR:
      return 'FREQ=YEARLY';
    default:
      return null;
  }
}

/**
 * Réaligne une règle quand l'ancre d'une série change de jour mural (décalage de série).
 * Préserve l'intention : preset hebdo mono-jour suit le nouveau jour, multi-jours subit
 * la même rotation que l'ancre, mensuel suit le nouveau jour du mois / nouvel ordinal
 * (« dernier » reste « dernier »). Retourne la forme canonique.
 */
export function realignRRuleToAnchor(rrule: string, oldAnchor: RecurrenceAnchor, newAnchor: RecurrenceAnchor): string {
  const rule = parseRRuleString(rrule);

  if (rule.freq === RecurrenceFreq.WEEKLY && rule.byWeekdays && rule.byWeekdays.length > 0) {
    if (rule.byWeekdays.length === 1) {
      rule.byWeekdays = [newAnchor.weekday];
    } else {
      const delta = (newAnchor.weekday - oldAnchor.weekday + 7) % 7;
      rule.byWeekdays = rule.byWeekdays.map((weekday): number => (weekday + delta) % 7);
    }
  } else if (rule.freq === RecurrenceFreq.MONTHLY) {
    if (rule.monthlyMode === RecurrenceMonthlyMode.BY_MONTHDAY) {
      rule.byMonthDay = newAnchor.monthday;
    } else if (rule.byOrdinal) {
      rule.byOrdinal = {
        ordinal: rule.byOrdinal.ordinal === -1 ? -1 : ordinalFromRecurrenceAnchor(newAnchor),
        weekday: newAnchor.weekday,
      };
    }
  }

  return serializeRecurrenceRule(rule);
}

const interpolateLabel = (template: string, params: Record<string, string | number>): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string): string => (key in params ? String(params[key]) : match));

const weekdayName = (weekday: number, locale: string): string => {
  // 2017-01-01 était un dimanche (getDay() === 0) : référence stable pour les noms ICU.
  const reference = new Date(Date.UTC(2017, 0, 1 + weekday, 12));
  return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(reference);
};

const WORKWEEK_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];

/**
 * Libellé humain d'une règle (« Toutes les 2 semaines le mardi et le jeudi, jusqu'au
 * 31 août 2026 »). Gabarits dans RecurrenceRuleLabelTranslation (fr-FR par défaut),
 * noms de jours et listes via ICU (Intl) — aucun texte en dur.
 */
export function formatRecurrenceRuleLabel(rrule: string, locale = 'fr-FR'): string {
  const rule = parseRRuleString(rrule);
  const labels =
    RecurrenceRuleLabelTranslation[locale as keyof typeof RecurrenceRuleLabelTranslation] ??
    RecurrenceRuleLabelTranslation['fr-FR'];

  let label: string;
  switch (rule.freq) {
    case RecurrenceFreq.DAILY:
      label =
        rule.interval === 1
          ? labels.RECURRENCE_LABEL_DAILY
          : interpolateLabel(labels.RECURRENCE_LABEL_DAILY_PLURAL, { interval: rule.interval });
      break;
    case RecurrenceFreq.WEEKLY: {
      const byWeekdays = rule.byWeekdays;
      const isEveryWeekday =
        rule.interval === 1 &&
        byWeekdays !== undefined &&
        byWeekdays.length === WORKWEEK_WEEKDAYS.length &&
        WORKWEEK_WEEKDAYS.every((weekday): boolean => byWeekdays.includes(weekday));
      if (isEveryWeekday) {
        label = labels.RECURRENCE_LABEL_EVERY_WEEKDAY;
        break;
      }
      label =
        rule.interval === 1
          ? labels.RECURRENCE_LABEL_WEEKLY
          : interpolateLabel(labels.RECURRENCE_LABEL_WEEKLY_PLURAL, { interval: rule.interval });
      if (rule.byWeekdays && rule.byWeekdays.length > 0) {
        const names = rule.byWeekdays.map((weekday): string => weekdayName(weekday, locale));
        const days = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(names);
        label += interpolateLabel(labels.RECURRENCE_LABEL_WEEKLY_DAYS_SUFFIX, { days });
      }
      break;
    }
    case RecurrenceFreq.MONTHLY: {
      label =
        rule.interval === 1
          ? labels.RECURRENCE_LABEL_MONTHLY
          : interpolateLabel(labels.RECURRENCE_LABEL_MONTHLY_PLURAL, { interval: rule.interval });
      if (rule.byOrdinal) {
        const ordinalKey =
          rule.byOrdinal.ordinal === -1
            ? 'RECURRENCE_ORDINAL_LAST'
            : (`RECURRENCE_ORDINAL_${rule.byOrdinal.ordinal}` as const);
        label += interpolateLabel(labels.RECURRENCE_LABEL_MONTHLY_ORDINAL_SUFFIX, {
          ordinal: labels[ordinalKey],
          weekday: weekdayName(rule.byOrdinal.weekday, locale),
        });
      } else if (rule.byMonthDay !== undefined) {
        // BYMONTHDAY=28..31;BYSETPOS=-1 ancré au 31 == toujours le dernier jour du mois.
        label +=
          rule.byMonthDay === 31
            ? labels.RECURRENCE_LABEL_MONTHLY_LAST_DAY_SUFFIX
            : interpolateLabel(labels.RECURRENCE_LABEL_MONTHLY_DAY_SUFFIX, { day: rule.byMonthDay });
      } else if (rule.weekOfMonth !== undefined) {
        const weekKey =
          rule.weekOfMonth === -1
            ? 'RECURRENCE_WEEK_OF_MONTH_LAST'
            : (`RECURRENCE_WEEK_OF_MONTH_${rule.weekOfMonth}` as const);
        label += interpolateLabel(labels.RECURRENCE_LABEL_MONTHLY_WEEK_SUFFIX, { week: labels[weekKey] });
      }
      break;
    }
    case RecurrenceFreq.YEARLY:
      label =
        rule.interval === 1
          ? labels.RECURRENCE_LABEL_YEARLY
          : interpolateLabel(labels.RECURRENCE_LABEL_YEARLY_PLURAL, { interval: rule.interval });
      break;
  }

  if (rule.endMode === RecurrenceEndMode.UNTIL_DATE && rule.until) {
    const year = Number(rule.until.slice(0, 4));
    const month = Number(rule.until.slice(4, 6));
    const day = Number(rule.until.slice(6, 8));
    // UNTIL est une date murale : rendue littéralement (UTC midi), indépendamment de la tz.
    const untilFormatted = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day, 12)));
    label += interpolateLabel(labels.RECURRENCE_LABEL_UNTIL_SUFFIX, { until: untilFormatted });
  } else if (rule.endMode === RecurrenceEndMode.AFTER_COUNT && rule.count !== undefined) {
    label += interpolateLabel(labels.RECURRENCE_LABEL_COUNT_SUFFIX, { count: rule.count });
  }

  return label;
}
